import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { AdminLayout } from "@/layouts/AdminLayout";
import { requireAdmin } from "@/lib/guards";
import { adminStatsQueryOptions } from "@/lib/route-query-options";
import { type AdminStats } from "@/services/admin";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Bell,
  Download,
  FileText,
  RefreshCw,
  TrendingUp,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";

export const Route = createFileRoute("/(app)/admin/")({
  beforeLoad: async (ctx) => requireAdmin(ctx),
  loader: ({ context }) => context.queryClient.ensureQueryData(adminStatsQueryOptions()),
  pendingMs: 200,
  pendingComponent: AdminDashboardPending,
  errorComponent: AdminDashboardError,
  component: AdminDashboard,
});

function AdminDashboard() {
  const { t } = useTranslation("admin");
  // Data is guaranteed by the loader - use suspense query for type safety
  const { data: stats } = useSuspenseQuery(adminStatsQueryOptions());

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const activationRate = stats.total_users > 0 ? Math.round((stats.active_users / stats.total_users) * 100) : 0;

  const chartData = [
    { date: "Mon", users: stats.new_users_today > 0 ? Math.max(1, stats.new_users_today - 5) : 2 },
    { date: "Tue", users: stats.new_users_today > 0 ? Math.max(1, stats.new_users_today - 3) : 4 },
    { date: "Wed", users: stats.new_users_today > 0 ? Math.max(1, stats.new_users_today - 2) : 3 },
    { date: "Thu", users: stats.new_users_today > 0 ? Math.max(1, stats.new_users_today - 1) : 5 },
    { date: "Fri", users: stats.new_users_today > 0 ? stats.new_users_today : 4 },
    { date: "Sat", users: stats.new_users_today > 0 ? Math.max(1, stats.new_users_today + 1) : 6 },
    { date: "Sun", users: stats.new_users_today > 0 ? Math.max(1, stats.new_users_today + 2) : 3 },
  ];

  const chartConfig = {
    users: {
      label: t("dashboard.userGrowth.chartLabel"),
      color: "hsl(var(--chart-1))",
    },
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">{t("dashboard.title")}</h2>
            <p className="text-muted-foreground text-sm">{t("dashboard.subtitle")}</p>
          </div>
          <Button
            variant="outline"
            className="hidden gap-2 sm:flex"
          >
            <Download className="h-4 w-4" />
            {t("dashboard.exportReport")}
          </Button>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title={t("dashboard.metrics.totalUsers")}
            value={stats.total_users}
            change={stats.new_users_this_week}
            changeLabel={t("dashboard.metrics.thisWeek")}
            trend="up"
            icon={<Users className="h-4 w-4" />}
          />
          <MetricCard
            title={t("dashboard.metrics.activeUsers")}
            value={stats.active_users}
            change={activationRate}
            changeLabel={t("dashboard.metrics.activationRate")}
            trend="up"
            suffix="%"
            icon={<Activity className="h-4 w-4" />}
          />
          <MetricCard
            title={t("dashboard.metrics.newToday")}
            value={stats.new_users_today}
            change={stats.new_users_this_week}
            changeLabel={t("dashboard.metrics.thisWeek")}
            trend={stats.new_users_today > 0 ? "up" : "neutral"}
            highlight
            icon={<TrendingUp className="h-4 w-4" />}
          />
          <MetricCard
            title={t("dashboard.metrics.activeSubscriptions")}
            value={stats.active_subscriptions}
            change={
              stats.total_subscriptions > 0
                ? Math.round((stats.active_subscriptions / stats.total_subscriptions) * 100)
                : 0
            }
            changeLabel={t("dashboard.metrics.ofTotal")}
            trend="up"
            suffix="%"
            icon={<BarChart3 className="h-4 w-4" />}
          />
        </div>

        {/* Charts Row */}
        <div className="grid gap-4 lg:grid-cols-2">
          {/* User Growth Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("dashboard.userGrowth.title")}</CardTitle>
              <CardDescription>{t("dashboard.userGrowth.subtitle")}</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer
                config={chartConfig}
                className="h-[200px] min-h-[200px] w-full min-w-0"
              >
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient
                      id="fillUsers"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="var(--color-users)"
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor="var(--color-users)"
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    allowDecimals={false}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Area
                    type="monotone"
                    dataKey="users"
                    stroke="var(--color-users)"
                    fill="url(#fillUsers)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ChartContainer>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("dashboard.platformSummary.title")}</CardTitle>
              <CardDescription>{t("dashboard.platformSummary.subtitle")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-muted flex items-center justify-between rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-info/10 rounded-full p-2">
                      <Users className="text-info h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t("dashboard.platformSummary.verifiedUsers")}</p>
                      <p className="text-muted-foreground text-xs">
                        {stats.total_users > 0 ? Math.round((stats.verified_users / stats.total_users) * 100) : 0}%{" "}
                        {t("dashboard.metrics.ofTotal")}
                      </p>
                    </div>
                  </div>
                  <p className="text-lg font-bold">{stats.verified_users}</p>
                </div>

                <div className="bg-muted flex items-center justify-between rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-success/10 rounded-full p-2">
                      <TrendingUp className="text-success h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t("dashboard.platformSummary.thisMonth")}</p>
                      <p className="text-muted-foreground text-xs">{t("dashboard.platformSummary.newRegistrations")}</p>
                    </div>
                  </div>
                  <p className="text-lg font-bold">{stats.new_users_this_month}</p>
                </div>

                <div className="bg-muted flex items-center justify-between rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/10 rounded-full p-2">
                      <FileText className="text-primary h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t("dashboard.platformSummary.totalFiles")}</p>
                      <p className="text-muted-foreground text-xs">
                        {t("dashboard.platformSummary.stored", { size: formatBytes(stats.total_file_size) })}
                      </p>
                    </div>
                  </div>
                  <p className="text-lg font-bold">{stats.total_files}</p>
                </div>

                <div className="bg-muted flex items-center justify-between rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-warning/10 rounded-full p-2">
                      <Bell className="text-warning h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t("dashboard.platformSummary.canceled")}</p>
                      <p className="text-muted-foreground text-xs">{t("dashboard.platformSummary.subscriptions")}</p>
                    </div>
                  </div>
                  <p className="text-lg font-bold">{stats.canceled_subscriptions}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users by Role */}
        {stats.users_by_role && Object.keys(stats.users_by_role).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("dashboard.usersByRole.title")}</CardTitle>
              <CardDescription>{t("dashboard.usersByRole.subtitle")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {Object.entries(stats.users_by_role).map(([role, count]) => (
                  <div
                    key={role}
                    className="flex items-center gap-3 rounded-lg border p-3"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs">{role.charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium capitalize">{role.replace("_", " ")}</p>
                      <p className="text-lg font-bold">{count}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}

function MetricCard({
  title,
  value,
  change,
  changeLabel,
  trend,
  highlight = false,
  suffix,
  icon,
}: {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  trend?: "up" | "down" | "neutral";
  highlight?: boolean;
  suffix?: string;
  icon?: React.ReactNode;
}) {
  return (
    <Card className={highlight ? "border-primary/30 bg-primary/5" : ""}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm font-medium">{title}</p>
          {icon && (
            <div
              className={`rounded-full p-1.5 ${highlight ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}
            >
              {icon}
            </div>
          )}
        </div>
        <p className={`mt-2 text-2xl font-bold ${highlight ? "text-primary" : ""}`}>{value}</p>
        {change !== undefined && (
          <div className="mt-1 flex items-center gap-1 text-xs">
            {trend === "up" && <ArrowUpRight className="text-success h-3 w-3" />}
            {trend === "down" && <ArrowDownRight className="text-destructive h-3 w-3" />}
            <span
              className={
                trend === "up" ? "text-success" : trend === "down" ? "text-destructive" : "text-muted-foreground"
              }
            >
              {change}
              {suffix}
            </span>
            <span className="text-muted-foreground">{changeLabel}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AdminDashboardPending() {
  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <div className="bg-muted h-8 w-48 animate-pulse rounded" />
          <div className="bg-muted/50 mt-2 h-4 w-64 animate-pulse rounded" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="bg-muted h-4 w-24 animate-pulse rounded" />
                <div className="bg-muted mt-2 h-8 w-16 animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          {[...Array(2)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <div className="bg-muted h-5 w-32 animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="bg-muted/50 h-[200px] animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}

function AdminDashboardError({ error, reset }: { error: Error; reset?: () => void }) {
  const { t } = useTranslation("admin");

  return (
    <AdminLayout>
      <Card className="border-destructive/30 bg-destructive/5">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="bg-destructive/10 rounded-full p-2">
              <AlertTriangle className="text-destructive h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-destructive">
                {t("dashboard.error.title", "Failed to load dashboard")}
              </CardTitle>
              <CardDescription className="text-destructive/80">
                {error.message || t("dashboard.error.default", "An unexpected error occurred")}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex gap-3">
          {reset && (
            <Button
              variant="outline"
              onClick={reset}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              {t("common.retry", "Try Again")}
            </Button>
          )}
          <Button
            variant="ghost"
            asChild
          >
            <Link to="/admin">{t("common.goBack", "Go Back")}</Link>
          </Button>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
